import React, { useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { X, UploadCloud, ArrowRight, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

const SYSTEM_FIELDS = new Set(["id", "created_at", "updated_at", "created_by", "updated_by", "tenant_id"]);

const ImportWizard = ({ isOpen, onClose, tableName, tableLabel, fields, apiBaseUrl, token, onComplete }) => {
  const [phase, setPhase] = useState("upload"); // upload -> mapping -> references -> preview -> done
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [file, setFile] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [columns, setColumns] = useState([]);
  const [mandatoryUnmapped, setMandatoryUnmapped] = useState([]);
  const [keyFields, setKeyFields] = useState([]);
  const [keyFieldToAdd, setKeyFieldToAdd] = useState("");

  const [refColumns, setRefColumns] = useState([]);
  const [decisions, setDecisions] = useState({}); // resolution_id -> {action, note}

  const [previewData, setPreviewData] = useState(null);
  const [skipErrors, setSkipErrors] = useState(true);
  const [summary, setSummary] = useState(null);

  const authHeaders = () => ({ Authorization: `Bearer ${token}` });
  const mappableFields = (fields || []).filter((f) => !SYSTEM_FIELDS.has(f.name));

  if (!isOpen) return null;

  const reset = () => {
    setPhase("upload"); setBusy(false); setError("");
    setFile(null); setJobId(null); setColumns([]); setMandatoryUnmapped([]);
    setKeyFields([]); setKeyFieldToAdd("");
    setRefColumns([]); setDecisions({});
    setPreviewData(null); setSkipErrors(true); setSummary(null);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleUpload = async () => {
    if (!file) return;
    setBusy(true); setError("");
    try {
      const form = new FormData();
      form.append("target_table", tableName);
      form.append("file", file);
      const res = await axios.post(`${apiBaseUrl}/import-jobs`, form, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      setJobId(res.data.import_job_id);
      setColumns(res.data.columns || []);
      setMandatoryUnmapped(res.data.mandatory_fields_unmapped || []);
      setPhase("mapping");
    } catch (e) {
      setError(e.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  const updateColumnMapping = (colId, targetField) => {
    setColumns((prev) => prev.map((c) => (c.id === colId ? { ...c, target_field: targetField || null } : c)));
  };

  const addKeyField = () => {
    if (!keyFieldToAdd || keyFields.includes(keyFieldToAdd)) return;
    setKeyFields((prev) => [...prev, keyFieldToAdd]);
    setKeyFieldToAdd("");
  };

  const removeKeyField = (name) => setKeyFields((prev) => prev.filter((k) => k !== name));

  const submitMapping = async () => {
    setBusy(true); setError("");
    try {
      await axios.patch(
        `${apiBaseUrl}/import-jobs/${jobId}/mapping`,
        { mappings: columns.map((c) => ({ import_job_column_id: c.id, target_field: c.target_field, transform: c.transform || null })), key_fields: keyFields },
        { headers: authHeaders() }
      );
      const res = await axios.post(`${apiBaseUrl}/import-jobs/${jobId}/resolve-references`, {}, { headers: authHeaders() });
      const groups = res.data.reference_columns || [];
      const needsReview = groups.some((g) => g.values.some((v) => v.match_status === "suggested" || v.match_status === "unmatched"));
      setRefColumns(groups);
      if (needsReview) {
        setPhase("references");
      } else {
        await runPreview();
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Mapping failed.");
    } finally {
      setBusy(false);
    }
  };

  const setDecision = (resolutionId, action, note) => {
    setDecisions((prev) => ({ ...prev, [resolutionId]: { action, note } }));
  };

  const submitResolutionsAndPreview = async () => {
    setBusy(true); setError("");
    try {
      const resolutions = Object.entries(decisions).map(([resolution_id, d]) => ({ resolution_id, action: d.action, note: d.note }));
      if (resolutions.length) {
        await axios.patch(`${apiBaseUrl}/import-jobs/${jobId}/resolutions`, { resolutions }, { headers: authHeaders() });
      }
      await runPreview();
    } catch (e) {
      setError(e.response?.data?.detail || "Resolving references failed.");
    } finally {
      setBusy(false);
    }
  };

  const runPreview = async () => {
    const res = await axios.post(`${apiBaseUrl}/import-jobs/${jobId}/preview`, {}, { headers: authHeaders() });
    setPreviewData(res.data);
    setPhase("preview");
  };

  const handleCommit = async () => {
    setBusy(true); setError("");
    try {
      const res = await axios.post(`${apiBaseUrl}/import-jobs/${jobId}/commit`, { skip_error_rows: skipErrors }, { headers: authHeaders() });
      setSummary(res.data.summary);
      setPhase("done");
      onComplete && onComplete();
    } catch (e) {
      setError(e.response?.data?.detail || "Commit failed.");
    } finally {
      setBusy(false);
    }
  };

  const stepLabel = { upload: "1. Upload", mapping: "1. Map fields", references: "2. Resolve references", preview: "3. Preview", done: "4. Done" }[phase];

  return ReactDOM.createPortal(
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={styles.headerIcon}><UploadCloud size={16} /></span>
            <span style={styles.crumb}>Import</span>
            <span style={styles.sep}>/</span>
            <span style={styles.crumbAccent}>{tableLabel || tableName}</span>
            <span style={styles.sep}>·</span>
            <span style={styles.crumb}>{stepLabel}</span>
          </div>
          <button onClick={handleClose} style={styles.iconBtn} title="Close"><X size={16} /></button>
        </div>

        <div style={styles.body}>
          {error && <div style={styles.errorBanner}><AlertTriangle size={14} /> {error}</div>}

          {phase === "upload" && (
            <div style={{ display: "grid", gap: 14 }}>
              <p style={styles.hint}>Upload a CSV or Excel (.xlsx) file. You'll review the field mapping and any reference-value matches before anything is written.</p>
              <input type="file" accept=".csv,.xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </div>
          )}

          {phase === "mapping" && (
            <div style={{ display: "grid", gap: 16 }}>
              {mandatoryUnmapped.length > 0 && (
                <div style={styles.warnBanner}>
                  <AlertTriangle size={14} /> Required field(s) not mapped yet: {mandatoryUnmapped.join(", ")}
                </div>
              )}
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Source column</th>
                    <th style={styles.th}>Sample values</th>
                    <th style={styles.th}></th>
                    <th style={styles.th}>Maps to</th>
                  </tr>
                </thead>
                <tbody>
                  {columns.map((c) => (
                    <tr key={c.id}>
                      <td style={styles.td}>{c.source_column}</td>
                      <td style={{ ...styles.td, color: "var(--muted, #5c6d6f)", fontSize: 12 }}>
                        {(c.sample_values || []).slice(0, 3).join(", ")}
                      </td>
                      <td style={styles.td}><ArrowRight size={14} /></td>
                      <td style={styles.td}>
                        <select
                          value={c.target_field || ""}
                          onChange={(e) => updateColumnMapping(c.id, e.target.value)}
                          style={styles.select}
                        >
                          <option value="">— Ignore this column —</option>
                          {mappableFields.map((f) => (
                            <option key={f.name} value={f.name}>{f.label || f.name}{f.is_mandatory ? " *" : ""}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div>
                <div style={styles.label}>Match existing records by (optional, for updates)</div>
                <p style={styles.hint}>Rows whose value on the first of these fields (in order) matches an existing record will be updated instead of created. Leave empty to always create new records.</p>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  {keyFields.map((k) => (
                    <span key={k} style={styles.chip}>
                      {mappableFields.find((f) => f.name === k)?.label || k}
                      <button onClick={() => removeKeyField(k)} style={styles.chipRemove}>×</button>
                    </span>
                  ))}
                  <select value={keyFieldToAdd} onChange={(e) => setKeyFieldToAdd(e.target.value)} style={styles.select}>
                    <option value="">Add a key field...</option>
                    {mappableFields.filter((f) => !keyFields.includes(f.name) && !f.reference_table).map((f) => (
                      <option key={f.name} value={f.name}>{f.label || f.name}</option>
                    ))}
                  </select>
                  <button onClick={addKeyField} style={styles.btnGhost} disabled={!keyFieldToAdd}>Add</button>
                </div>
              </div>
            </div>
          )}

          {phase === "references" && (
            <div style={{ display: "grid", gap: 18 }}>
              <p style={styles.hint}>Some values didn't exactly match existing records. Review each one below.</p>
              {refColumns.map((group) => (
                <div key={group.import_job_column_id}>
                  <div style={styles.label}>{group.source_column} → {group.reference_table}</div>
                  <table style={styles.table}>
                    <tbody>
                      {group.values.map((v) => {
                        const decided = decisions[v.resolution_id];
                        const isTerminal = v.match_status === "exact";
                        return (
                          <tr key={v.resolution_id}>
                            <td style={styles.td}>
                              <strong>{v.raw_value}</strong>
                              <div style={{ fontSize: 11, color: "var(--muted, #5c6d6f)" }}>{v.affected_row_count} row(s)</div>
                            </td>
                            <td style={styles.td}>
                              {isTerminal ? (
                                <span style={styles.okBadge}><CheckCircle2 size={13} /> Matched existing</span>
                              ) : v.match_status === "suggested" ? (
                                <span>Suggested: <strong>{v.suggested_label}</strong> ({Math.round(v.suggested_similarity * 100)}%)</span>
                              ) : (
                                <span style={{ color: "var(--danger, #dc2626)" }}>No match found</span>
                              )}
                            </td>
                            <td style={styles.td}>
                              {!isTerminal && (
                                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                  {v.match_status === "suggested" && (
                                    <button
                                      style={decided?.action === "use_existing" ? styles.btnPrimarySm : styles.btnGhostSm}
                                      onClick={() => setDecision(v.resolution_id, "use_existing", "accepted suggested match")}
                                    >
                                      Accept
                                    </button>
                                  )}
                                  <button
                                    style={decided?.action === "create_new" ? styles.btnPrimarySm : styles.btnGhostSm}
                                    onClick={() => setDecision(v.resolution_id, "create_new", "")}
                                  >
                                    Create new
                                  </button>
                                  <button
                                    style={decided?.action === "ignore" ? styles.btnPrimarySm : styles.btnGhostSm}
                                    onClick={() => setDecision(v.resolution_id, "ignore", "")}
                                  >
                                    Ignore
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}

          {phase === "preview" && previewData && (
            <div style={{ display: "grid", gap: 14 }}>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <div style={styles.countCard}><div style={styles.countNum}>{previewData.counts.will_create || 0}</div><div style={styles.countLabel}>Will create</div></div>
                <div style={styles.countCard}><div style={styles.countNum}>{previewData.counts.will_update || 0}</div><div style={styles.countLabel}>Will update</div></div>
                <div style={{ ...styles.countCard, borderColor: previewData.counts.error ? "var(--danger, #dc2626)" : undefined }}>
                  <div style={styles.countNum}>{previewData.counts.error || 0}</div><div style={styles.countLabel}>Errors</div>
                </div>
              </div>

              {previewData.issues.length > 0 && (
                <div>
                  <div style={styles.label}>Issues ({previewData.issue_count_total})</div>
                  <table style={styles.table}>
                    <tbody>
                      {previewData.issues.map((i, idx) => (
                        <tr key={idx}>
                          <td style={styles.td}>Row {i.row_number}</td>
                          <td style={styles.td}>{i.field_name || ""}</td>
                          <td style={styles.td}>{i.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {previewData.counts.error > 0 && (
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                  <input type="checkbox" checked={skipErrors} onChange={(e) => setSkipErrors(e.target.checked)} />
                  Skip the {previewData.counts.error} row(s) with errors and commit the rest
                </label>
              )}
            </div>
          )}

          {phase === "done" && summary && (
            <div style={{ display: "grid", gap: 10, justifyItems: "center", padding: "20px 0" }}>
              <CheckCircle2 size={40} color="var(--brand, #0d7d6f)" />
              <div style={{ fontWeight: 800, fontSize: 16 }}>Import complete</div>
              <div style={{ display: "flex", gap: 16 }}>
                <div><strong>{summary.created}</strong> created</div>
                <div><strong>{summary.updated}</strong> updated</div>
                {summary.failed > 0 && <div style={{ color: "var(--danger, #dc2626)" }}><strong>{summary.failed}</strong> failed</div>}
                {summary.skipped > 0 && <div><strong>{summary.skipped}</strong> skipped</div>}
              </div>
            </div>
          )}
        </div>

        <div style={styles.footer}>
          {phase !== "upload" && phase !== "done" && (
            <button style={styles.btnGhost} onClick={handleClose} disabled={busy}>Cancel</button>
          )}
          {phase === "upload" && (
            <>
              <button style={styles.btnGhost} onClick={handleClose}>Cancel</button>
              <button style={styles.btnPrimary} onClick={handleUpload} disabled={!file || busy}>
                {busy ? <Loader2 size={14} className="spin" /> : "Upload & Analyze"}
              </button>
            </>
          )}
          {phase === "mapping" && (
            <button style={styles.btnPrimary} onClick={submitMapping} disabled={busy}>
              {busy ? "Working..." : "Continue"}
            </button>
          )}
          {phase === "references" && (
            <button style={styles.btnPrimary} onClick={submitResolutionsAndPreview} disabled={busy}>
              {busy ? "Working..." : "Continue to Preview"}
            </button>
          )}
          {phase === "preview" && (
            <button style={styles.btnPrimary} onClick={handleCommit} disabled={busy || (previewData.counts.error > 0 && !skipErrors)}>
              {busy ? "Committing..." : "Commit"}
            </button>
          )}
          {phase === "done" && (
            <button style={styles.btnPrimary} onClick={handleClose}>Close</button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

const styles = {
  overlay: { position: "fixed", inset: 0, backgroundColor: "rgba(8, 20, 19, 0.6)", backdropFilter: "blur(2px)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000, padding: 16 },
  modal: { backgroundColor: "#ffffff", width: "92vw", maxWidth: 900, maxHeight: "90vh", display: "flex", flexDirection: "column", borderRadius: 20, boxShadow: "0 24px 60px rgba(8,95,84,.18)", overflow: "hidden" },
  header: { padding: "14px 18px", borderBottom: "1px solid #dfe7e5", display: "flex", alignItems: "center", justifyContent: "space-between", background: "linear-gradient(90deg, #085f54, #0d7d6f)", color: "#e5e7eb" },
  headerIcon: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.14)", color: "#fff" },
  crumb: { fontWeight: 600, opacity: 0.85, color: "#e6f4f1" },
  crumbAccent: { fontWeight: 800, color: "#ffffff" },
  sep: { opacity: 0.5 },
  body: { padding: 18, overflowY: "auto", flex: 1 },
  footer: { padding: 12, borderTop: "1px solid #dfe7e5", background: "#fff", display: "flex", justifyContent: "flex-end", gap: 8 },
  iconBtn: { display: "inline-flex", alignItems: "center", justifyContent: "center", background: "transparent", border: "1px solid rgba(229,231,235,.4)", color: "#e5e7eb", borderRadius: 10, padding: "6px 10px", cursor: "pointer" },
  btnPrimary: { border: "1px solid #0d7d6f", background: "#0d7d6f", color: "#fff", padding: "10px 14px", borderRadius: 10, fontWeight: 800, cursor: "pointer" },
  btnGhost: { border: "1px solid #dfe7e5", background: "#fff", color: "#1e2e30", padding: "10px 14px", borderRadius: 10, fontWeight: 700, cursor: "pointer" },
  btnPrimarySm: { border: "1px solid #0d7d6f", background: "#0d7d6f", color: "#fff", padding: "5px 10px", borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 12 },
  btnGhostSm: { border: "1px solid #dfe7e5", background: "#fff", color: "#1e2e30", padding: "5px 10px", borderRadius: 8, fontWeight: 600, cursor: "pointer", fontSize: 12 },
  select: { padding: "8px 10px", borderRadius: 8, border: "1px solid #dfe7e5", background: "#fff", outline: "none" },
  label: { fontSize: 11, fontWeight: 700, color: "#5c6d6f", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 },
  hint: { fontSize: 13, color: "#5c6d6f", margin: 0 },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { textAlign: "left", fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", color: "#5c6d6f", padding: "6px 8px", borderBottom: "1px solid #dfe7e5" },
  td: { padding: "8px", borderBottom: "1px solid #eef2f1", fontSize: 13, verticalAlign: "top" },
  errorBanner: { display: "flex", alignItems: "center", gap: 8, background: "#fef2f2", color: "#991b1b", padding: "10px 12px", borderRadius: 10, fontSize: 13, marginBottom: 12 },
  warnBanner: { display: "flex", alignItems: "center", gap: 8, background: "#fffbeb", color: "#92400e", padding: "10px 12px", borderRadius: 10, fontSize: 13 },
  okBadge: { display: "inline-flex", alignItems: "center", gap: 6, color: "#0d7d6f", fontWeight: 700, fontSize: 13 },
  chip: { display: "inline-flex", alignItems: "center", gap: 6, background: "#f1f5f4", borderRadius: 999, padding: "4px 10px", fontSize: 12, fontWeight: 700 },
  chipRemove: { border: "none", background: "transparent", cursor: "pointer", fontWeight: 800, color: "#5c6d6f" },
  countCard: { border: "1px solid #dfe7e5", borderRadius: 12, padding: "10px 18px", textAlign: "center", minWidth: 100 },
  countNum: { fontSize: 22, fontWeight: 800 },
  countLabel: { fontSize: 11, color: "#5c6d6f", textTransform: "uppercase" },
};

export default ImportWizard;
